import { MenuItem, Select, Stack, Typography } from '@mui/material';
import type { RoiId } from '~/domain/changes/ids';
import StructureSwatch from '~/components/Structures/StructureSwatch';
import { FIELD_SIZING } from '~/theme/fieldSizing';
import { menuProps } from './selectMenuProps';

export interface StructureSelectOption {
    roiId: RoiId;
    /** What the row and the trigger read, usually the structure's name. */
    label: string;
    /** CSS colour of the structure's swatch. */
    color: string;
    /** Listed, so the user sees it, but not pickable. */
    disabled?: boolean;
}

interface StructureSelectProps {
    /** The picked structure, or null while none is picked. */
    value: RoiId | null;
    onChange: (roiId: RoiId) => void;
    options: readonly StructureSelectOption[];
    'aria-label': string;
    /** What the trigger reads while nothing is picked. */
    placeholder?: string;
    disabled?: boolean;
}

/**
 * Picks one structure, each listed by its swatch and name. The trigger
 * shows the picked structure the same way, or the placeholder while none
 * is picked.
 */
export default function StructureSelect({
    value,
    onChange,
    options,
    'aria-label': ariaLabel,
    placeholder,
    disabled = false,
}: StructureSelectProps) {
    return (
        <Select
            value={value ?? ''}
            onChange={(e) => {
                const picked = options.find((o) => o.roiId === e.target.value);
                if (picked) {
                    onChange(picked.roiId);
                }
            }}
            size="small"
            fullWidth
            displayEmpty
            disabled={disabled}
            inputProps={{ 'aria-label': ariaLabel }}
            MenuProps={menuProps}
            renderValue={() => {
                const picked = options.find((o) => o.roiId === value);
                if (!picked) {
                    return (
                        <Typography
                            variant="inherit"
                            color="text.secondary"
                            noWrap
                        >
                            {placeholder}
                        </Typography>
                    );
                }
                return (
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={`${FIELD_SIZING.gap}px`}
                    >
                        <StructureSwatch color={picked.color} />
                        <Typography variant="inherit" noWrap>
                            {picked.label}
                        </Typography>
                    </Stack>
                );
            }}
        >
            {options.map((o) => (
                <MenuItem key={o.roiId} value={o.roiId} disabled={o.disabled}>
                    <StructureSwatch color={o.color} />
                    {o.label}
                </MenuItem>
            ))}
        </Select>
    );
}
